import makeExceptionLogger from '~/libs/exception-logger';
import getOptions, { Destination } from '~/libs/analytics/utils/options';
import Me from './me';
import window from './global';

interface Options {
  destination?: Destination;
}

interface Traits {
  source?: string;
}

const isDataDogSynthetics = () => window._DATADOG_SYNTHETICS_BROWSER === true;

/**
 * Thin wrapper function around Segment Analytics track method
 * @deprecated use `useTrackAction` instead
 * @param name  Name of the action the current user performed.
 * @param properties  A bag of metadata to send to Segment/Amplitude.
 * @param options  Additional options to set things like destination groups.
 */
export const trackAction: (
  name: string,
  properties?: object,
  options?: Options,
) => void = (name, properties, options = {}): void => {
  try {
    if (isDataDogSynthetics()) return;
    const pageName = window?.circleci?.analytics?.pageName;
    if (pageName) {
      properties = { pageName, ...properties };
    }
    window.analytics?.track(name, properties, getOptions(options));
  } catch {
    /* istanbul ignore next */ makeExceptionLogger().error(
      'Track Action',
      name,
      properties || {},
    );
  }
};

/**
 * Thin wrapper function around Segment Analytics identify method
 * @deprecated use `Analytic` component instead
 * @param userData  A dictionary of traits you know about the user, like their email, name, source.
 * @param traits  Data about the current user session including source.
 */
export const trackUser = (userData: Me, traits: Traits = {}): void => {
  try {
    if (isDataDogSynthetics()) return;
    window.analytics?.identify(userData.id, traits);
  } catch {
    /* istanbul ignore next */ makeExceptionLogger().error(
      'Track User',
      userData.id,
      traits,
    );
  }
};

const setGlobalPageName = (pageName: string) => {
  window.circleci = window.circleci ?? {};
  window.circleci.analytics = window.circleci.analytics ?? {};
  window.circleci.analytics.pageName = pageName;
};

/**
 * Thin wrapper function around Segment Analytics page method. It will merge in windowWidth & windowHeight properties into the object that's passed in.
 * Properties passed in with those names will take precedence over the ones that the function provides for itself.
 * @deprecated use `Page` component instead
 * @param name  Name of the page the current user viewed.
 * @param properties  A bag of metadata to send to Segment/Amplitude.
 */
export const trackPage = (name: string, properties: object): void => {
  setGlobalPageName(name);
  try {
    if (isDataDogSynthetics()) return;
    const buildId = (window as any)?.next?.router?.pageLoader?.buildId;
    const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

    window.analytics?.page(name, {
      buildId,
      windowWidth,
      windowHeight,
      ...properties,
    });
  } catch {
    /* istanbul ignore next */ makeExceptionLogger().error(
      'Track Page',
      name,
      properties,
    );
  }
};
